import type { PathmaticsMonthlyRow } from './pathmaticsData.schema';

export type MediaMetric = 'spend' | 'impressions' | 'cpm';

export interface SpendImpressions {
  spend: number;
  impressions: number;
}

/** Spend per thousand impressions. Zero impressions yields zero, never Infinity or NaN. */
export function cpmFrom(spend: number, impressions: number): number {
  return impressions > 0 ? (spend * 1000) / impressions : 0;
}

export function sumRows(rows: SpendImpressions[]): SpendImpressions {
  let spend = 0;
  let impressions = 0;
  for (const r of rows) {
    spend += r.spend;
    impressions += r.impressions;
  }
  return { spend, impressions };
}

export function groupSums<T extends SpendImpressions>(
  rows: T[],
  key: (row: T) => string,
): Map<string, SpendImpressions> {
  const grouped = new Map<string, SpendImpressions>();
  for (const r of rows) {
    const k = key(r);
    const current = grouped.get(k) ?? { spend: 0, impressions: 0 };
    current.spend += r.spend;
    current.impressions += r.impressions;
    grouped.set(k, current);
  }
  return grouped;
}

export function metricValue(totals: SpendImpressions, metric: MediaMetric): number {
  if (metric === 'spend') return totals.spend;
  if (metric === 'impressions') return totals.impressions;
  return cpmFrom(totals.spend, totals.impressions);
}

/** Recharts wants flat rows, so each advertiser becomes a key alongside `month`. */
export interface TrendPoint {
  month: string;
  [advertiser: string]: string | number;
}

export function trendPoints(
  rows: PathmaticsMonthlyRow[],
  advertisers: string[],
  months: string[],
  metric: MediaMetric,
): TrendPoint[] {
  const byAdvertiserMonth = groupSums(rows, (r) => `${r.advertiser}|${r.month}`);
  return months.map((month) => {
    const point: TrendPoint = { month };
    for (const advertiser of advertisers) {
      const totals = byAdvertiserMonth.get(`${advertiser}|${month}`) ?? {
        spend: 0,
        impressions: 0,
      };
      point[advertiser] = Math.round(metricValue(totals, metric) * 100) / 100;
    }
    return point;
  });
}
